import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";
import { Search, Plus, Database, Warehouse as WarehouseIcon, ChevronRight, FilePlus2, ShoppingCart, Clock } from "lucide-react";

function isPhasedOutProduct(item) {
    const name = String(item?.item_name || item?.description || "").toUpperCase();
    return name.includes("PHASED OUT") || name.includes("PHASED-OUT") || name.includes("[PHASED OUT]");
}

export default function Inquiry() {
    const navigate = useNavigate();
    const { hasPermission, user } = useAuth();

    const [schemas, setSchemas] = useState([]);
    const [schemaId, setSchemaId] = useState("");
    const [warehouses, setWarehouses] = useState([]);
    const [wh, setWh] = useState("");

    const [searchQ, setSearchQ] = useState("");
    const [searchHits, setSearchHits] = useState([]);
    const [searching, setSearching] = useState(false);

    const [selected, setSelected] = useState(null);
    const [qty, setQty] = useState(50);
    const [batch, setBatch] = useState("");
    const [loosePcs, setLoosePcs] = useState(false);  // E42: order loose pieces
    const [result, setResult] = useState(null);
    const [irKind, setIrKind] = useState(null);
    const [loading, setLoading] = useState(false);
    const [poLoading, setPoLoading] = useState(false);
    const [logRefresh, setLogRefresh] = useState(0);

    const selectedSchema = useMemo(() => schemas.find((s) => s.id === schemaId), [schemas, schemaId]);
    const canViewStock = searchHits.some((p) => p.can_view_stock) || result?.can_view_stock;

    const suggestedTonalityMix = useMemo(() => {
        const raw =
            result?.suggested_tonality_mix ||
            result?.suggested_allocation ||
            result?.alternative_tonality_suggestions ||
            [];

        return raw.map((x) => ({
            ...x,
            quantity:
                x?.quantity ??
                x?.suggested_quantity ??
                x?.available_quantity ??
                0,
        }));
    }, [result]);

    const suggestionNeeded = Boolean(result?.suggestion_needed || suggestedTonalityMix.length > 0);

    const phasedOut = Boolean(isPhasedOutProduct(selected) || isPhasedOutProduct(result));
    const phasedOutNeedsProjectRegistration = Boolean(
        phasedOut &&
        result &&
        ["not_enough", "without_stock"].includes(result.status)
    );

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/me/assigned-schemas");
                setSchemas(data);
            } catch (e) {
                toast.error(formatApiError(e));
            }
        })();
    }, []);

    useEffect(() => {
        setWh("");
        setWarehouses([]);
        setSearchHits([]);
        setSelected(null);
        setResult(null);

        if (!schemaId) return;

        (async () => {
            try {
                const { data } = await api.get(`/me/assigned-warehouses?schemaId=${schemaId}`);
                setWarehouses(data);
            } catch (e) {
                toast.error(formatApiError(e));
            }
        })();
    }, [schemaId]);

    useEffect(() => {
        setSearchHits([]);
        setSelected(null);
        setResult(null);
    }, [wh]);

    useEffect(() => {
        if (!schemaId || !wh) return;

        const handle = setTimeout(async () => {
            setSearching(true);
            try {
                const { data } = await api.get("/inquiry/items/search", {
                    params: { schemaId, warehouseCode: wh, q: searchQ },
                });
                setSearchHits(data);
            } catch (e) {
                // silent during typing
            } finally {
                setSearching(false);
            }
        }, 250);

        return () => clearTimeout(handle);
    }, [schemaId, wh, searchQ]);

    const runCheck = async () => {
        if (!selected) return toast.error("Select an item first");
        if (!qty || Number(qty) <= 0) return toast.error("Please enter the requested quantity before running the availability check.");

        setLoading(true);

        try {
            const { data } = await api.post("/inquiry/availability-check", {
                schema_id: schemaId,
                item_code: selected.item_code,
                warehouse_code: wh,
                quantity: Number(qty),
                batch_code: batch || null,
                search_q: searchQ || selected.item_code,
            });

            setResult(data);
            setLogRefresh((k) => k + 1);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const addToCart = async () => {
        if (!selected) return;

        try {
            let finalQty = Number(qty);

            const pcsPerBox = Number(
                result?.packaging_quantity ??
                selected?.packaging_quantity ??
                0
            );

            // r42: loose-box PCS ordering — Samples / Subway / Mosaic may be sold by the piece,
            // so skip the full-box rounding for those categories (others still round to a box).
            const _looseText = `${result?.item_group_name || ""} ${result?.item_group || ""} ${result?.item_name || ""} ${result?.description || ""} ${selected?.item_name || ""} ${selected?.item_group || ""} ${selected?.item_group_name || ""}`.toUpperCase();
            // By-piece is the default. ONLY Tiles/Moroccan/Vinyl/Customized Mosaic are BY BOX and
            // round up to a full box; everything else (incl. plain Mosaic, Slabs, Subway) is by piece.
            const _isByBox = /TILE|MOROCCAN|VINYL|CUSTOMIZED MOSAIC/.test(_looseText);
            if (pcsPerBox > 0 && _isByBox) {
                const roundedQty =
                    Math.ceil(finalQty / pcsPerBox) * pcsPerBox;

                if (roundedQty !== finalQty) {
                    toast.info(
                        "This item is sold by full box. Quantity will be rounded up to the nearest full box quantity."
                    );

                    finalQty = roundedQty;
                    setQty(String(roundedQty));
                }
            }

            await api.post("/cart/items", {
                schema_id: schemaId,
                item_code: selected.item_code,
                warehouse_code: wh,
                quantity: finalQty,
                batch_code: batch || null,
            });

            toast.success("Added to cart");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const createAdvancedPO = async () => {
        if (!result) return;

        setPoLoading(true);

        try {
            const available = Number(result.available_stock ?? result.total_available ?? 0);
            const requested = Number(result.requested_quantity ?? qty ?? 0);

            await api.post("/advanced-po-drafts", {
                inquiry_id: result.inquiry_id || result.id || `${result.item_code}-${Date.now()}`,
                schema_id: schemaId,
                item_code: result.item_code,
                warehouse_code: result.warehouse_code,
                requested_quantity: requested,
                available_quantity: available,
                shortage_quantity: Math.max(0, requested - available),
                eta: result.eta || null,
                reason: "Not enough stock / out of stock",
                remarks: result.remarks || "",
            });

            toast.success("Advanced PO draft created and sent to Inquiry + Purchasing.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setPoLoading(false);
        }
    };

    return (
        <div className="p-3 md:p-6 space-y-5 md:space-y-6" data-testid="inquiry-page">
            <div>
                <div className="sc-section-title">Inquiry Engine</div>
                <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Stock & Batch Availability</h1>
                <p className="text-sm text-muted-foreground">
                    Schema → Warehouse → Item → Quantity → Run check. Allocation selects the least number of tonalities.
                </p>
            </div>

            <div className="flex items-center gap-2 text-xs overflow-x-auto pb-1">
                <Step n={1} active={!schemaId} done={!!schemaId}>Select Schema</Step>
                <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
                <Step n={2} active={!!schemaId && !wh} done={!!wh}>Select Warehouse</Step>
                <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
                <Step n={3} active={!!wh && !selected} done={!!selected}>Search Item</Step>
                <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
                <Step n={4} active={!!selected && !result} done={!!result}>Run Check</Step>
            </div>

            <div className="sc-card p-4 md:p-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-2">
                        <Database className="w-3.5 h-3.5" /> Schema / Database
                    </label>

                    <select
                        className="sc-input mt-1"
                        value={schemaId}
                        onChange={(e) => setSchemaId(e.target.value)}
                        data-testid="inquiry-schema"
                    >
                        <option value="">Select schema…</option>
                        {schemas.map((s) => (
                            <option key={s.id} value={s.id}>
                                {s.display_name}
                            </option>
                        ))}
                    </select>

                    {selectedSchema && (
                        <div className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-2">
                            <StatusBadge status="active" className="!text-[10px]" />
                            Schema selected
                        </div>
                    )}
                </div>

                <div>
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-2">
                        <WarehouseIcon className="w-3.5 h-3.5" /> Warehouse
                    </label>

                    <select
                        className="sc-input mt-1"
                        value={wh}
                        onChange={(e) => setWh(e.target.value)}
                        disabled={!schemaId}
                        data-testid="inquiry-warehouse"
                    >
                        <option value="">Select warehouse…</option>
                        {warehouses.map((w) => (
                            <option key={w.warehouse_code} value={w.warehouse_code}>
                                {w.display_name || w.short_name || w.name || w.warehouse_code}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {schemaId && wh && (
                <div className="sc-card">
                    <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-4">
                        <div className="flex-1 max-w-xl flex items-center gap-2.5 h-11 px-3.5 rounded-lg bg-background border-2 border-foreground shadow-sm transition focus-within:ring-4 focus-within:ring-foreground/15">
                            <Search className="w-5 h-5 text-foreground shrink-0" />
                            <input
                                className="flex-1 bg-transparent border-0 outline-none text-sm font-medium placeholder:text-muted-foreground placeholder:font-normal"
                                placeholder="Search by item code, name, or description…"
                                value={searchQ}
                                onChange={(e) => setSearchQ(e.target.value)}
                                data-testid="inquiry-search"
                            />
                        </div>

                        <div className="text-xs text-muted-foreground">
                            {searching ? "Searching…" : `${searchHits.length} matches`}
                        </div>
                    </div>

                    <div className="overflow-x-auto max-h-[340px] overflow-y-auto">
                        <table className="w-full sc-table min-w-[860px]">
                            <thead>
                                <tr>
                                    <th>Item code</th>
                                    <th>Name</th>
                                    <th>Description</th>
                                    <th>Size</th>
                                    <th className="text-right">Pcs/Box</th>
                                    <th className="text-right">Weight/Pc</th>
                                    {canViewStock && <th className="text-right">Available</th>}
                                    <th className="text-right">Dealer Price</th>
                                </tr>
                            </thead>

                            <tbody>
                                {searchHits.length === 0 && (
                                    <tr>
                                        <td colSpan={canViewStock ? 8 : 7} className="text-center text-muted-foreground py-6">
                                            No items match.
                                        </td>
                                    </tr>
                                )}

                                {searchHits.map((p) => {
                                    const isSel = selected?.item_code === p.item_code;
                                    return (
                                        <tr
                                            key={p.item_code}
                                            onClick={() => setSelected(p)}
                                            className={`cursor-pointer ${isSel ? "bg-secondary" : ""}`}
                                            data-testid={`inquiry-hit-${p.item_code}`}
                                        >
                                            <td className="font-mono text-xs">{p.item_code}</td>
                                            <td>
                                                {p.item_name}
                                                {p.no_stock && (
                                                    <span className="ml-2 inline-block text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200">
                                                        No stock
                                                    </span>
                                                )}
                                            </td>
                                            <td className="text-muted-foreground text-xs">{p.description}</td>
                                            <td className="font-mono text-xs">{p.size}</td>
                                            <td className="sc-num">{p.packaging_quantity || "—"}</td>
                                            <td className="sc-num">{p.weight_per_pc ? `${p.weight_per_pc} ${p.weight_unit}` : "—"}</td>
                                            {canViewStock && <td className="sc-num">{p.no_stock ? "0" : (p.available_stock ?? "—")}</td>}
                                            <td className="sc-num">{p.dealer_price != null ? Number(p.dealer_price).toFixed(2) : "—"}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {selected && (
                <div className="sc-card p-4 md:p-5 grid grid-cols-1 md:grid-cols-5 gap-4" data-testid="inquiry-run-panel">
                    <div className="md:col-span-2">
                        <div className="text-xs uppercase text-muted-foreground tracking-wider">Selected item</div>
                        <div className="font-mono text-sm mt-0.5">{selected.item_code}</div>
                        <div className="text-sm">{selected.item_name}</div>
                    </div>

                    <div>
                        <label className="text-xs font-medium text-muted-foreground">Requested Qty</label>
                        <input
                            type="number"
                            min={1}
                            step="1"
                            className="sc-input mt-1 font-mono"
                            value={qty}
                            onChange={(e) => setQty(e.target.value)}
                            data-testid="inquiry-qty"
                            required
                        />
                        {(() => {
                            const _t = `${result?.item_group_name || ""} ${result?.item_group || ""} ${result?.item_name || ""} ${result?.description || ""} ${selected?.item_name || ""} ${selected?.item_group || ""} ${selected?.item_group_name || ""}`.toUpperCase();
                            const _byBox = /TILE|MOROCCAN|VINYL|CUSTOMIZED MOSAIC/.test(_t);
                            return (
                                <label className="flex items-center gap-1.5 mt-1.5 text-[11px] text-muted-foreground">
                                    <input type="checkbox" checked={!_byBox} disabled readOnly data-testid="inquiry-loose" />
                                    {_byBox ? "Sold by box — by-piece not allowed for this category" : "Sold by piece (automatic for this category)"}
                                </label>
                            );
                        })()}
                    </div>

                    <div>
                        <label className="text-xs font-medium text-muted-foreground">Tonality Only (optional)</label>
                        <input
                            className="sc-input mt-1 font-mono"
                            placeholder="e.g. **25061A"
                            value={batch}
                            onChange={(e) => setBatch(e.target.value)}
                            data-testid="inquiry-batch"
                        />
                    </div>

                    <div className="flex items-end">
                        <button
                            onClick={runCheck}
                            disabled={loading}
                            className="h-9 w-full inline-flex items-center justify-center gap-2 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-50"
                            data-testid="inquiry-submit"
                        >
                            <Search className="w-4 h-4" />
                            {loading ? "Checking…" : "Run check"}
                        </button>
                    </div>
                </div>
            )}

            {result && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 md:p-6">
                    <div className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-auto" data-testid="inquiry-result">
                        <div className="px-4 md:px-5 py-4 border-b border-border flex items-start md:items-center justify-between gap-3 sticky top-0 bg-background z-10">
                            <div className="min-w-0">
                                <div className="text-xs uppercase text-muted-foreground tracking-wider">Availability Result</div>
                                <h2 className="text-base md:text-lg font-semibold break-words">
                                    {result.item_code} — {result.item_name}
                                </h2>
                                <div className="text-xs text-muted-foreground mt-1">
                                    {result.schema_display_name} · {result.warehouse_display_name || result.warehouse_name || "Warehouse"}
                                </div>
                            </div>

                            <div className="flex items-center gap-2 shrink-0">
                                <StatusBadge status={result.status} />
                                <button onClick={() => setResult(null)} className="h-9 px-4 rounded-md bg-foreground text-background text-sm">
                                    Close
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-px bg-border">
                            <Info label="Description" value={result.description} />
                            <Info label="Size" value={result.size} />
                            <Info label="Inquired Qty" value={result.inquired_quantity ?? result.requested_quantity} mono />
                            <Info label="Pcs per Box" value={result.packaging_quantity} />
                            <Info
                                label="Estimated Boxes"
                                value={
                                    result.packaging_quantity
                                        ? (Number(result.inquired_quantity || result.requested_quantity || 0) / Number(result.packaging_quantity || 1)).toFixed(2)
                                        : "—"
                                }
                                mono
                            />
                            <Info label="Weight per Pc" value={result.weight_per_pc != null ? `${result.weight_per_pc} ${result.weight_unit}` : "—"} />
                            <Info
                                label="Total Weight"
                                value={
                                    result.weight_per_pc
                                        ? `${(Number(result.inquired_quantity || result.requested_quantity || 0) * Number(result.weight_per_pc || 0)).toFixed(2)} ${result.weight_unit || ""}`
                                        : "—"
                                }
                                mono
                            />
                            <Info label="Dealer Price" value={result.dealer_price != null ? Number(result.dealer_price).toFixed(2) : "—"} mono />
                            {result.promo_price != null && ((user?.business_mode === "non_organic" && (user?.price_visibility === "distributor" || user?.price_visibility === "dealers")) || user?.role === "admin") && <Info label="Promo Price" value={Number(result.promo_price).toFixed(2)} mono valueClass="text-red-600 font-bold" />}
                            {result.can_view_stock && <Info label="Available" value={result.total_available} mono />}
                            {result.eta && !phasedOutNeedsProjectRegistration && <Info label="ETA" value={result.eta} />}
                        </div>

                        <div className="p-4 md:p-5 border-t border-border">
                            <div className="rounded-md bg-secondary/60 border border-border p-4">
                                <div className="text-xs uppercase tracking-wider text-muted-foreground">Availability Summary</div>
                                <div className="text-sm font-medium mt-1">
                                    {result.status === "not_enough"
                                        ? "Not enough stock"
                                        : result.status === "without_stock"
                                            ? "Out of stock"
                                            : result.tonality_summary || (result.tonality_count ? `Available in ${result.tonality_count} tonalities` : "Available")}
                                </div>
                            </div>
                        </div>

                        {result.show_all_tonalities && (
                            <div className="p-4 md:p-5 border-t border-border">
                                <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-emerald-900">
                                    <div className="font-semibold text-sm">
                                        Vivari clearance — all tonalities visible
                                    </div>
                                    <div className="text-xs mt-1">
                                        This is a Vivari warehouse. Every available tonality is shown and any required-tonality filter is not applied.
                                    </div>

                                    {result.all_tonalities?.length > 0 ? (
                                        <div className="mt-3 overflow-hidden rounded border border-emerald-200 bg-white">
                                            <table className="w-full text-xs">
                                                <thead>
                                                    <tr className="bg-emerald-100">
                                                        <th className="text-left px-2 py-1">Tonality</th>
                                                        <th className="text-right px-2 py-1">Available Qty</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {result.all_tonalities.map((t, i) => (
                                                        <tr key={`${t.tonality}-${i}`} className="border-t border-emerald-100">
                                                            <td className="px-2 py-1 font-mono">{t.tonality || "—"}</td>
                                                            <td className="px-2 py-1 text-right font-mono">
                                                                {Number(t.quantity || 0).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot>
                                                    <tr className="border-t border-emerald-200 bg-emerald-50 font-semibold">
                                                        <td className="px-2 py-1">Total ({result.all_tonalities.length} tonalities)</td>
                                                        <td className="px-2 py-1 text-right font-mono">
                                                            {Number(result.total_available_all_tonalities || 0).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-xs mt-2 italic">
                                            No tonalities currently in stock for this item in this warehouse.
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="sticky bottom-0 bg-background border-t border-border p-3 flex flex-col md:flex-row gap-2 justify-end">
                            {suggestionNeeded && suggestedTonalityMix.length > 0 && (
                                <div className="col-span-full rounded-lg border border-blue-300 bg-blue-50 p-4 text-blue-900">
                                    <div className="font-semibold text-sm">
                                        {result.requested_tonality
                                            ? "Requested tonality is insufficient"
                                            : result.status === "not_enough"
                                                ? "Not enough stock — available by tonality"
                                                : "Available by tonality"}
                                    </div>
                                    {result.requested_tonality && (
                                        <div className="text-xs mt-1">
                                            Requested tonality: <span className="font-mono">{result.requested_tonality}</span>
                                            {" "}· Available: <span className="font-mono">{Number(result.requested_tonality_available || 0).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}</span>
                                        </div>
                                    )}

                                    <div className="mt-3 text-xs font-semibold uppercase tracking-wider">
                                        {result.requested_tonality ? "Suggested Tonality Mix" : "Available Tonalities"}
                                    </div>

                                    <div className="mt-2 overflow-hidden rounded border border-blue-200 bg-white">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="bg-blue-100">
                                                    <th className="text-left px-2 py-1">Tonality</th>
                                                    <th className="text-right px-2 py-1">Qty</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {suggestedTonalityMix.map((x, i) => (
                                                    <tr key={`${x.tonality}-${i}`} className="border-t border-blue-100">
                                                        <td className="px-2 py-1 font-mono">{x.tonality || "—"}</td>
                                                        <td className="px-2 py-1 text-right font-mono">
                                                            {Number(x.quantity || 0).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="text-xs mt-2">
                                        If the customer accepts mixed tonalities, continue by adding this item to cart. Otherwise, create an Advanced PO.
                                    </div>
                                </div>
                            )}

                            {phasedOutNeedsProjectRegistration && (
                                <div className="col-span-full rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">
                                    <div className="font-semibold text-sm">
                                        PHASED OUT PRODUCT — Project Registration Required
                                    </div>
                                    <div className="text-xs mt-1">
                                        This item is tagged as phased out. Sales Agent should create or link a Project Registration Form before quotation processing.
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => navigate("/project-registrations")}
                                        className="mt-3 h-9 px-3 rounded-md bg-amber-700 text-white text-sm hover:opacity-90"
                                    >
                                        Create / View Project Registration
                                    </button>
                                </div>
                            )}

                            {!["not_enough", "without_stock"].includes(result.status) && hasPermission("cart.write") && (
                                <button
                                    onClick={addToCart}
                                    className="h-10 inline-flex items-center justify-center gap-2 rounded-md border border-border text-sm hover:bg-secondary px-4"
                                    data-testid="inquiry-add-to-cart"
                                >
                                    <Plus className="w-4 h-4" /> Add to cart
                                </button>
                            )}

                            {["not_enough", "without_stock"].includes(result.status) && (
                                (!/\d{4}-\d{1,2}-\d{1,2}/.test(String(result.eta || ""))) ? (
                                    <button onClick={() => setIrKind("lead_time")} data-testid="inquiry-leadtime"
                                        className="h-10 inline-flex items-center justify-center gap-2 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-sm hover:bg-amber-100 px-4">
                                        <Clock className="w-4 h-4" /> Lead Time Inquiry → PD
                                    </button>
                                ) : (
                                    <button onClick={() => setIrKind("eta_qty")} data-testid="inquiry-etaqty"
                                        className="h-10 inline-flex items-center justify-center gap-2 rounded-md border border-sky-300 bg-sky-50 text-sky-800 text-sm hover:bg-sky-100 px-4">
                                        <Clock className="w-4 h-4" /> Lead Time Inquiry → ID
                                    </button>
                                )
                            )}

                            <button
                                onClick={() => navigate("/cart")}
                                className="h-10 inline-flex items-center justify-center gap-2 rounded-md bg-foreground text-background text-sm px-4 hover:opacity-90"
                                data-testid="inquiry-view-cart"
                            >
                                <ShoppingCart className="w-4 h-4" /> View Cart
                            </button>

                            {irKind && <IrRequestModal kind={irKind} result={result} schemaId={schemaId} qty={qty} onClose={() => setIrKind(null)} navigate={navigate} />}

                            {/* Advanced PO Draft intentionally hidden from Inquiry result modal. */}
                        </div>
                    </div>
                </div>
            )}

            <InquiryLogPanel refreshKey={logRefresh} />
        </div>
    );
}

function availabilityBadge(a) {
    if (a === "no_stock") return { label: "No stock", cls: "bg-rose-100 text-rose-700 border-rose-200" };
    if (a === "phased_out") return { label: "Phased out", cls: "bg-amber-100 text-amber-800 border-amber-200" };
    return { label: "In stock", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" };
}

function InquiryLogPanel({ refreshKey }) {
    const { user } = useAuth();
    const canView = user?.role === "admin" || user?.role === "inquiry_dept";
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!canView) return;
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/inquiry/logs", { params: { limit: 40 } });
                if (alive) setLogs(Array.isArray(data?.logs) ? data.logs : []);
            } catch (e) {
                if (alive) setLogs([]);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [refreshKey, canView]);

    if (!canView) return null;   // Inquiry Department + admin only

    return (
        <div className="sc-card p-4 md:p-5" data-testid="inquiry-log-panel">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Inquiry Log</div>
                    <div className="text-sm text-muted-foreground">Recent item inquiries — who checked what, and whether it was available.</div>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full sc-table min-w-[760px]">
                    <thead>
                        <tr>
                            <th>Agent</th>
                            <th>Item</th>
                            <th className="text-right">Qty</th>
                            <th>Searched</th>
                            <th>Warehouse</th>
                            <th>Status</th>
                            <th>When</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">Loading…</td></tr>}
                        {!loading && logs.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">No inquiries logged yet.</td></tr>}
                        {!loading && logs.map((l) => {
                            const b = availabilityBadge(l.availability);
                            return (
                                <tr key={l.id}>
                                    <td className="font-medium">{l.agent}</td>
                                    <td>
                                        <div className="font-mono text-xs">{l.item_code || "—"}</div>
                                        <div className="text-xs text-muted-foreground">{l.item_name || ""}</div>
                                    </td>
                                    <td className="sc-num">{l.quantity ?? "—"}</td>
                                    <td className="text-xs text-muted-foreground">{l.search_q || "—"}</td>
                                    <td className="text-xs">{l.warehouse_name}</td>
                                    <td><span className={`inline-block text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${b.cls}`}>{b.label}</span></td>
                                    <td className="text-xs text-muted-foreground whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : "—"}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Step({ n, children, active, done }) {
    let cls = "border-border text-muted-foreground";
    if (done) cls = "bg-foreground text-background border-foreground";
    else if (active) cls = "border-foreground text-foreground";

    return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border shrink-0 ${cls}`}>
            <span className="font-mono text-[10px]">{n}</span> {children}
        </span>
    );
}

function Info({ label, value, mono, valueClass }) {
    return (
        <div className="bg-background px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`text-sm mt-0.5 ${mono ? "font-mono tabular-nums" : ""} ${valueClass || ""}`}>{value ?? "—"}</div>
        </div>
    );
}


function IrRequestModal({ kind, result, schemaId, qty, onClose, navigate }) {
    const { user } = useAuth();
    const isLead = kind === "lead_time";
    const [f, setF] = useState({ mobile_number: "", date_needed: "", comments: "" });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
    const q0 = Number(qty || result?.requested_quantity || 0);
    const ppb = Number(result?.packaging_quantity || 0);
    // Same parameters as adding to the Sales Cart (Sir Lynx 2026-09-30): a box-sold item's
    // requested qty rounds UP to the nearest full box before it ever reaches PD/ID, instead of
    // going in exact and only being split into a box+loose-pc breakdown for display.
    const _byBox = /TILE|MOROCCAN|VINYL|CUSTOMIZED MOSAIC/.test(
        `${result?.item_group_name || ""} ${result?.item_group || ""} ${result?.item_name || ""} ${result?.description || ""}`.toUpperCase());
    const q = (_byBox && ppb > 0) ? Math.ceil(q0 / ppb) * ppb : q0;
    const bxs = ppb > 0 ? Math.floor(q / ppb) : 0;
    const pcs = ppb > 0 ? Math.round(q - bxs * ppb) : 0;
    const rounded = q !== q0;
    const submit = async () => {
        setBusy(true);
        try {
            await api.post("/inquiry-requests", {
                kind, schema_id: schemaId, schema_name: result?.schema_name,
                warehouse_code: result?.warehouse_code, branch: user?.branch,
                item_code: result?.item_code, item_name: result?.item_name, size: result?.size,
                quantity: q, bxs, pcs, eta: result?.eta || "",
                mobile_number: f.mobile_number, date_needed: f.date_needed, comments: f.comments,
            });
            toast.success(isLead ? "Lead Time Inquiry sent to Purchasing." : "ETA & QTY Verification sent to Inquiry Dept.");
            onClose();
            navigate("/inquiry-requests");
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-background rounded-lg border border-border w-full max-w-md p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
                <div className="font-semibold">{isLead ? "Lead Time Inquiry (to Purchasing)" : "ETA & QTY Verification (to Inquiry Dept)"}</div>
                <div className="text-xs text-muted-foreground font-mono">{result?.item_code} · {result?.item_name} · qty {q} ({bxs} bx {pcs} pc){result?.eta ? ` · ETA ${result.eta}` : ""}</div>
                {rounded && <div className="text-[11px] text-amber-700">Sold by full box. Rounded up from {q0} to {q}.</div>}
                {isLead && (
                    <>
                        <div><label className="text-xs text-muted-foreground">Mobile Number (Sales Agent)</label><input className="sc-input mt-1 h-9" value={f.mobile_number} onChange={(e) => set("mobile_number", e.target.value)} placeholder="09xx xxx xxxx" /></div>
                        <div><label className="text-xs text-muted-foreground">Date Needed by Client</label><input type="date" className="sc-input mt-1 h-9" value={f.date_needed} onChange={(e) => set("date_needed", e.target.value)} /></div>
                    </>
                )}
                <div><label className="text-xs text-muted-foreground">Other Questions &amp; Comments</label><input className="sc-input mt-1 h-9" value={f.comments} onChange={(e) => set("comments", e.target.value)} placeholder="Optional" /></div>
                <div className="flex justify-end gap-2">
                    <button className="px-3 py-1.5 rounded-md border border-border text-sm" onClick={onClose}>Cancel</button>
                    <button className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-60" disabled={busy} onClick={submit}>{busy ? "Sending…" : (isLead ? "Submit to PD" : "Submit to ID")}</button>
                </div>
            </div>
        </div>
    );
}
